/**
 * Split-order held-for-review notification routing.
 *
 * A split-location order that carries none of this org's auto-accept tags is
 * held behind a SPLIT_ORDER issue until a human accepts it — and until now
 * nothing told anyone. This card is where an org says where that reminder goes.
 *
 * WHY IT IS ITS OWN CARD WITH ITS OWN SAVE, not part of the page's main form:
 * the surrounding form writes `organizations.settings.inventoryPush.notifications`
 * via a read-merge-write on `updateOrganization`, which REPLACES the whole
 * settings blob. This routing deliberately lives in its own column
 * (`prod_settings.notification_routing`) so it can never be collateral damage
 * of an unrelated save — the #1483 brands-wipe shape. Two stores, two saves,
 * no shared mutable object. It is also mounted OUTSIDE that form (nested forms
 * are invalid HTML) and its buttons are explicitly type="button".
 *
 * English-only on purpose: this page has zero `t()` calls, and adding locale
 * keys for one card would either look inconsistent or drag the whole 1100-line
 * page into i18n.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { GitBranch, Loader2, Save, Send } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

const CATEGORY = 'split_order_held' as const;

interface RouteShape {
  enabled?: boolean;
  webhook_url?: string | null;
  channel?: string | null;
}

export function SplitOrderNotificationCard() {
  const utils = trpc.useUtils();
  const { data, isLoading, isError } = trpc.productionSettings.getNotificationRouting.useQuery();

  const [enabled, setEnabled] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [channel, setChannel] = useState('');

  // Hydrate ONLY from a resolved payload. Seeding state on `undefined` would
  // render a configured org as "off", and a Save from that state would then
  // persist enabled:false with a blank destination — silently switching off
  // the alerts because a GET happened to fail.
  useEffect(() => {
    if (!data) return;
    const routing = (data.notification_routing ?? {}) as Record<string, RouteShape | undefined>;
    // An absent category means "not configured" — never "inherit from the 3PL",
    // which would silently point this org's alerts at someone else's channel.
    const route = routing[CATEGORY];
    setEnabled(route?.enabled === true);
    setWebhookUrl(route?.webhook_url ?? '');
    setChannel(route?.channel ?? '');
  }, [data]);

  const save = trpc.productionSettings.updateNotificationRouting.useMutation({
    onSuccess: () => {
      toast.success('Split order notifications saved');
      void utils.productionSettings.getNotificationRouting.invalidate();
    },
    // Error toasts persist until dismissed (DESIGN.md); a save that silently
    // failed is how an org ends up believing it is covered when it is not.
    onError: (err) => toast.error(err.message, { duration: Infinity }),
  });

  const sendTest = trpc.productionSettings.testNotificationRouting.useMutation({
    onSuccess: () => toast.success('Test message sent'),
    onError: (err) => toast.error(err.message, { duration: Infinity }),
  });

  const hasDestination = webhookUrl.trim().length > 0 || channel.trim().length > 0;
  // Inline error rather than a toast (DESIGN.md): the problem is a specific
  // field, and a toast makes the reader hunt for which one.
  const destinationError = enabled && !hasDestination;

  const handleSave = () => {
    if (destinationError) return;
    save.mutate({
      category: CATEGORY,
      enabled,
      webhook_url: webhookUrl.trim() || null,
      channel: channel.trim() || null,
    });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-100">
            <GitBranch className="h-5 w-5 text-amber-600" aria-hidden="true" />
          </div>
          <div className="flex-1">
            <CardTitle className="text-base">Split Orders Held for Review</CardTitle>
            <CardDescription>
              A reminder every 30 minutes listing your split-location orders that are still
              waiting for someone to accept the split. Quiet when nothing is held, and quiet
              again once the list stops changing.
            </CardDescription>
          </div>
          <Switch
            aria-label="Enable split order notifications"
            checked={enabled}
            disabled={isLoading}
            onCheckedChange={setEnabled}
          />
        </div>
      </CardHeader>

      {enabled && (
        <CardContent className="space-y-6 border-t pt-6">
          <div className="space-y-2">
            <Label htmlFor="split-order-webhook-url">Webhook URL</Label>
            <Input
              id="split-order-webhook-url"
              type="url"
              placeholder="https://hooks.slack.com/services/T.../B.../..."
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              className="font-mono text-sm"
            />
            <p className="text-sm text-muted-foreground">
              A Slack incoming webhook posts to the one channel it was created for. Leave blank
              to use a channel name below instead.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="split-order-channel">Channel</Label>
            <Input
              id="split-order-channel"
              placeholder="#order-issues"
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
            />
            <p className="text-sm text-muted-foreground">
              Used only when no webhook URL is set, and it needs your Slack app connected. A
              webhook URL always wins.
            </p>
          </div>
        </CardContent>
      )}

      <CardContent className={enabled ? 'pt-0' : 'border-t pt-6'}>
        {isError && (
          <p className="mb-3 text-sm text-destructive">
            Could not load the current settings. Reload before saving, or you may overwrite them.
          </p>
        )}
        {destinationError && (
          <p className="mb-3 text-sm text-destructive">
            Add a webhook URL or a channel before enabling.
          </p>
        )}
        <div className="flex gap-2">
          <Button
            type="button"
            onClick={handleSave}
            // Never save from a state that was never loaded — that is how a
            // configured destination gets blanked by a failed GET.
            disabled={save.isPending || isLoading || isError || destinationError}
          >
            {save.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 motion-safe:animate-spin" aria-hidden="true" />
            ) : (
              <Save className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            Save changes
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => sendTest.mutate({ category: CATEGORY })}
            // Tests the SAVED route, so it is meaningless until something is saved.
            disabled={sendTest.isPending || isLoading || isError || !enabled}
          >
            {sendTest.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 motion-safe:animate-spin" aria-hidden="true" />
            ) : (
              <Send className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            Send test
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
